import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/lib/useSite";
import { PublicLayout, PageIntro, FinalCta, Reveal, ImageReveal } from "@/components/site";
import { SERVICE_PAGES, catalogEntry, TWILIGHT_BEFORE, TWILIGHT_IMG } from "@/lib/siteContent";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { BOOKING_URL } from "@/lib/booking";
import { ProjectCard } from "@/components/home/HomeWork";

export default function Services() {
  const { settings } = useSite();
  return (
    <PublicLayout>
      <PageIntro label="Services" title="What we do." text="Photography. Film. Aerial. Digital. Everything a listing needs, from one team." />
      <section data-testid="services-list" className="px-6 sm:px-10 lg:px-16 py-20 md:py-28 grid md:grid-cols-2 gap-x-6 gap-y-16 bg-[#0B0C0E]">
        {SERVICE_PAGES.map((s, i) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block">
            <ImageReveal src={s.image} alt={s.label} className={i % 3 === 0 ? "aspect-[4/5]" : "aspect-[4/3]"} imgClassName="zoom-slow" />
            <div className="flex items-baseline justify-between mt-5">
              <h2 className="font-display text-4xl md:text-5xl text-white">{s.label}</h2>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#E5B869]">View</span>
            </div>
            <p className="text-zinc-400 text-sm mt-3 max-w-md">{s.headline}</p>
          </Link>
        ))}
      </section>
      <FinalCta image={settings.cta_image_url} />
    </PublicLayout>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const { settings, projects, loaded } = useSite();
  const [catalog, setCatalog] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get("/services").then((r) => setCatalog(r.data)).catch(() => {}); }, [slug]);

  const page = SERVICE_PAGES.find((s) => s.slug === slug);
  if (!page) return <PublicLayout><PageIntro label="Services" title="Service not found." /></PublicLayout>;
  const entry = catalogEntry(catalog, page.catalogId);
  const others = SERVICE_PAGES.filter((s) => s.slug !== slug);
  const photographyProjects = slug === "photography" ? projects.filter((project) => project.slug === "nassau-ny" || /long island|nassau|hampton/i.test(project.title || "")).sort((a, b) => Number(b.slug === "nassau-ny") - Number(a.slug === "nassau-ny")).slice(0, 2) : [];

  return (
    <PublicLayout>
      <section className="pt-20 bg-[#0B0C0E]">
        {slug === "twilight" ? (
          <div className="md:h-[85vh] max-h-[900px] overflow-hidden"><BeforeAfterSlider before={TWILIGHT_BEFORE} after={TWILIGHT_IMG} className="!aspect-auto h-full min-h-[320px]" /></div>
        ) : (
          <ImageReveal src={page.image} alt={page.label} className="aspect-[16/9] md:h-[85vh] md:aspect-auto" />
        )}
      </section>
      <section data-testid="service-detail" className="px-6 sm:px-10 lg:px-16 py-20 md:py-32 grid lg:grid-cols-12 gap-10 bg-[#0B0C0E]">
        <Reveal className="lg:col-span-7">
          <div className="eyebrow">{page.label}</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[.9] max-w-3xl text-white">{page.headline}</h1>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:pt-16" delay={0.1}>
          <p data-testid="service-description" className="text-zinc-400 leading-relaxed">{entry?.desc}</p>
          {entry?.meta && <p data-testid="service-meta" className="text-sm text-[#C8B9A6] mt-4">{entry.meta}</p>}
          <a href={BOOKING_URL} data-testid="service-book-btn" className="btn-dark mt-10">Book a Shoot</a>
        </Reveal>
      </section>
      {slug === "photography" && <section data-testid="photography-project-galleries" className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10 bg-[#121418]"><div className="mx-auto max-w-[1440px]"><Reveal><div className="eyebrow">Recent photography</div><h2 className="font-display text-5xl sm:text-6xl text-white mt-5">Long Island &amp; the Hamptons.</h2><p className="max-w-xl mt-5 text-sm leading-relaxed text-zinc-400">A closer look at two recent listing galleries, from bright Nassau exteriors to East Hampton interiors.</p></Reveal><div className="mt-12 grid gap-8 lg:grid-cols-2">{photographyProjects.map((project) => <ProjectCard key={project.id} p={project} testid={`photography-gallery-${project.slug}`} aspect="aspect-video" editorial />)}{!loaded && <p data-testid="photography-galleries-loading" className="text-sm text-zinc-500">Loading recent photography.</p>}{loaded && !photographyProjects.length && <p data-testid="photography-galleries-empty" className="text-sm text-zinc-500">Recent photography galleries are coming soon.</p>}</div></div></section>}
      <section className="px-6 sm:px-10 lg:px-16 pb-24 border-t border-white/10 pt-16 bg-[#121418]">
        <div className="eyebrow mb-6">More services</div>
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          {others.map((s) => <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-related-${s.slug}`} className="font-display text-3xl md:text-4xl text-white/45 hover:text-[#E5B869] transition-colors duration-300">{s.label}</Link>)}
        </div>
      </section>
      <FinalCta image={settings.cta_image_url} />
    </PublicLayout>
  );
}
